import { api } from "./client";

/**
 * Scheduling (decision 0022): agendas, appointment types, free times, appointments, each person's
 * connected calendars, the notices and the WhatsApp calendar. Shapes mirror the API.
 */
export const WEEKDAYS = [
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
] as const;
export type Weekday = (typeof WEEKDAYS)[number];
export interface TimeRange {
  from: string;
  to: string;
}
export type Weekly = Partial<Record<Weekday, TimeRange[]>>;

export interface AgendaSettings {
  quietHours: { from: string; to: string } | null;
  eventTitle: string;
  eventDetails: boolean;
  inviteAll: boolean;
  inviteContact: boolean;
  freeFormInWindow: boolean;
  contactTimeZone: boolean;
  onExternalChange: "notify" | "notify_and_ask" | "ignore";
  team: { push: boolean; dailySummaryAt: string | null; emailInvite: boolean };
}

export interface AgendaMember {
  memberId: string;
  name: string;
  weekly: Weekly | null;
  priority: number;
  isActive: boolean;
}

export interface Agenda {
  id: string;
  name: string;
  kind: "team" | "person" | "resource";
  teamId: string | null;
  timezone: string;
  hoursMode: "business_hours" | "own" | "per_person";
  businessHoursId: string | null;
  weekly: Weekly;
  holidays: "national" | "national_optional" | null;
  settings: AgendaSettings;
  isActive: boolean;
  pausedUntil: string | null;
  sort: number;
  members: AgendaMember[];
  /** Active appointment types. */
  types: number;
  /** Appointments still ahead. */
  upcoming: number;
}

export interface DateOverride {
  id: string;
  memberId: string | null;
  dateFrom: string;
  dateTo: string;
  /** No ranges: closed. */
  ranges: TimeRange[];
  note: string | null;
}

export type LocationSettings =
  | { kind: "none" }
  | {
      kind: "address";
      address: string;
      latitude: number | null;
      longitude: number | null;
    }
  | { kind: "contact_field"; field: string }
  | {
      kind: "online";
      provider: "google_meet" | "teams" | "link";
      link: string | null;
    }
  | { kind: "phone" }
  | { kind: "to_agree" };

export interface IntakeField {
  key: string;
  label: string;
  kind: "text" | "choice";
  options: string[];
  required: boolean;
  saveToField: string | null;
}

export interface AppointmentType {
  id: string;
  agendaId: string;
  name: string;
  color: string | null;
  description: string | null;
  durationMin: number;
  stepMin: number;
  bufferBeforeMin: number;
  bufferAfterMin: number;
  minNoticeMin: number;
  maxDaysAhead: number;
  maxPerDay: number | null;
  maxOpenPerContact: number | null;
  location: LocationSettings;
  assignment: {
    mode: "round_robin" | "owner" | "fixed" | "collective";
    memberId: string | null;
    preferOwner: boolean;
  };
  intake: IntakeField[];
  confirmation: {
    required: boolean;
    replyByMin: number;
    onNoReply: "notify" | "release" | "ask_again";
  };
  /** Minutes before the start until which the contact may move it; null: they cannot. */
  rescheduleUntilMin: number | null;
  cancelUntilMin: number | null;
  onBook: {
    tagIds: string[];
    crmActivity: boolean;
    closeWithOutcome: string | null;
  };
  isActive: boolean;
  sort: number;
}

/** A type in the pickers (assistant, flow block, booking modal), with its agenda. */
export interface TypeChoice extends AppointmentType {
  agendaName: string;
  agendaActive: boolean;
  timezone: string;
}

export type NoticeKind =
  | "confirmation"
  | "confirm_request"
  | "reminder"
  | "rescheduled"
  | "cancelled"
  | "no_show";
export type NoticeTiming =
  | { when: "on_event" }
  | { when: "before"; minutes: number }
  | { when: "days_before"; days: number; at: string }
  | { when: "after"; minutes: number };

export interface NoticeRule {
  id: string;
  agendaId: string;
  kind: NoticeKind;
  enabled: boolean;
  timing: NoticeTiming;
  templateName: string | null;
  /** The free-form text with {{name}}, {{type}}, {{date}}, {{time}}, {{place}}, {{with}}, {{link}}, {{previous}}. */
  body: string | null;
  sort: number;
}

export interface CalendarStatus {
  id: string;
  memberId: string;
  provider: "google" | "microsoft" | "caldav" | "ics";
  account: string;
  status:
    "active" | "needs_consent" | "needs_reauth" | "error" | "disconnected";
  lastSyncedAt: string | null;
}

export interface CalendarChoice {
  id: string;
  name: string;
  primary: boolean;
  canWrite: boolean;
  color?: string;
  busy: boolean;
  write: boolean;
}
export interface CalendarConnection extends CalendarStatus {
  calendars: CalendarChoice[];
  lastError: string | null;
}

export interface AgendaDetail {
  agenda: Agenda;
  types: AppointmentType[];
  overrides: DateOverride[];
  notices: NoticeRule[];
  calendars: CalendarStatus[];
}

export interface Slot {
  start: string;
  end: string;
  memberIds: string[];
}

export type AppointmentStatus =
  "booked" | "confirmed" | "cancelled" | "attended" | "no_show";
export interface Appointment {
  id: string;
  agendaId: string;
  agendaName: string;
  typeId: string;
  typeName: string;
  typeColor: string | null;
  contactId: string;
  contactName: string;
  conversationId: string | null;
  channelId: string | null;
  memberId: string | null;
  memberName: string | null;
  holderIds: string[];
  status: AppointmentStatus;
  startsAt: string;
  endsAt: string;
  timezone: string;
  location: string | null;
  locationKind: string;
  onlineUrl: string | null;
  answers: Record<string, string>;
  notes: string | null;
  source: "assistant" | "flow" | "member" | "api" | "contact";
  createdByKind: string;
  createdById: string | null;
  confirmedAt: string | null;
  cancelledAt: string | null;
  cancelledByKind: string | null;
  cancelReason: string | null;
  rescheduleCount: number;
  previousStartsAt: string | null;
  publicToken: string | null;
  createdAt: string;
}

export interface AppointmentDetail {
  appointment: Appointment;
  notices: Array<{
    kind: string;
    dueAt: string;
    status: "pending" | "sent" | "skipped" | "failed";
    sentAt: string | null;
    error: string | null;
  }>;
  events: Array<{
    provider: string;
    memberId: string;
    status: string;
    error: string | null;
  }>;
  history: Array<{
    action: string;
    actorKind: string;
    actorId: string | null;
    at: string;
  }>;
}

export interface BusyBlock {
  memberId: string;
  provider: string;
  start: string;
  end: string;
}

export interface SchedulingReport {
  period: { from: string; to: string; timezone: string };
  booked: number;
  previousBooked: number;
  confirmedRate: number;
  confirmedByButtonRate: number;
  noShowRate: number;
  medianMinutesToBook: number | null;
  metaCost: {
    micros: number;
    currency: string | null;
    perAppointmentMicros: number;
  };
  /** Each assistant and flow by name; the team, contacts and integrations as a whole. */
  whoBooked: Array<{ source: string; name: string | null; count: number }>;
  bySource: {
    assistant: number;
    flow: number;
    member: number;
    contact: number;
    api: number;
  };
  outcomes: {
    attended: number;
    rescheduled: number;
    cancelled: number;
    noShow: number;
  };
  members: Array<{
    memberId: string;
    name: string;
    booked: number;
    confirmedRate: number;
    noShowRate: number;
    sales: number;
  }>;
}

const base = (slug: string) => `/v1/c/${slug}`;

export const schedulingApi = {
  agendas: (slug: string) =>
    api<{ agendas: Agenda[] }>(`${base(slug)}/agendas`),
  agenda: (slug: string, id: string) =>
    api<AgendaDetail>(`${base(slug)}/agendas/${id}`),
  createAgenda: (
    slug: string,
    body: Partial<Agenda> & { memberIds?: string[] },
  ) =>
    api<{ agenda: Agenda }>(`${base(slug)}/agendas`, { method: "POST", body }),
  patchAgenda: (slug: string, id: string, body: Partial<Agenda>) =>
    api<{ agenda: Agenda }>(`${base(slug)}/agendas/${id}`, {
      method: "PATCH",
      body,
    }),
  deleteAgenda: (slug: string, id: string) =>
    api<{ ok: true }>(`${base(slug)}/agendas/${id}`, { method: "DELETE" }),
  setMembers: (
    slug: string,
    id: string,
    members: Array<
      Pick<AgendaMember, "memberId" | "weekly" | "priority" | "isActive">
    >,
  ) =>
    api<{ agenda: Agenda }>(`${base(slug)}/agendas/${id}/members`, {
      method: "PUT",
      body: { members },
    }),
  addOverride: (slug: string, id: string, body: Omit<DateOverride, "id">) =>
    api<{ override: DateOverride }>(`${base(slug)}/agendas/${id}/overrides`, {
      method: "POST",
      body,
    }),
  removeOverride: (slug: string, id: string, overrideId: string) =>
    api<{ ok: true }>(`${base(slug)}/agendas/${id}/overrides/${overrideId}`, {
      method: "DELETE",
    }),

  types: (slug: string) =>
    api<{ types: TypeChoice[] }>(`${base(slug)}/appointment-types`),
  createType: (
    slug: string,
    agendaId: string,
    body: Partial<AppointmentType>,
  ) =>
    api<{ type: AppointmentType }>(`${base(slug)}/agendas/${agendaId}/types`, {
      method: "POST",
      body,
    }),
  patchType: (slug: string, id: string, body: Partial<AppointmentType>) =>
    api<{ type: AppointmentType }>(`${base(slug)}/appointment-types/${id}`, {
      method: "PATCH",
      body,
    }),
  deleteType: (slug: string, id: string) =>
    api<{ ok: true }>(`${base(slug)}/appointment-types/${id}`, {
      method: "DELETE",
    }),

  addNotice: (slug: string, agendaId: string, body: Partial<NoticeRule>) =>
    api<{ notice: NoticeRule }>(`${base(slug)}/agendas/${agendaId}/notices`, {
      method: "POST",
      body,
    }),
  patchNotice: (
    slug: string,
    agendaId: string,
    id: string,
    body: Partial<NoticeRule>,
  ) =>
    api<{ notice: NoticeRule }>(
      `${base(slug)}/agendas/${agendaId}/notices/${id}`,
      {
        method: "PATCH",
        body,
      },
    ),
  deleteNotice: (slug: string, agendaId: string, id: string) =>
    api<{ ok: true }>(`${base(slug)}/agendas/${agendaId}/notices/${id}`, {
      method: "DELETE",
    }),
  noticeTemplates: (slug: string, agendaId: string) =>
    api<{
      templates: Array<{
        account_id: string;
        name: string;
        language: string;
        status: string;
        rejection_reason: string | null;
      }>;
      accounts: Array<{ id: string; name: string | null }>;
    }>(`${base(slug)}/agendas/${agendaId}/notices/templates`),
  createNoticeTemplate: (
    slug: string,
    agendaId: string,
    id: string,
    body: { languages: Array<"pt-BR" | "es" | "en">; map: boolean },
  ) =>
    api<{
      results: Array<{
        accountId: string;
        name: string;
        language: string;
        status: string;
        error?: string;
      }>;
    }>(`${base(slug)}/agendas/${agendaId}/notices/${id}/template`, {
      method: "POST",
      body,
    }),

  slots: (
    slug: string,
    typeId: string,
    query: {
      from?: string;
      to?: string;
      limit?: number;
      contactId?: string;
      memberId?: string;
      exclude?: string;
    } = {},
  ) =>
    api<{ timezone: string; durationMin: number; slots: Slot[] }>(
      `${base(slug)}/appointment-types/${typeId}/slots`,
      { query },
    ),
  appointments: (
    slug: string,
    query: {
      from?: string;
      to?: string;
      agendaId?: string;
      memberId?: string;
      mine?: number;
      status?: string;
    } = {},
  ) =>
    api<{ appointments: Appointment[] }>(`${base(slug)}/appointments`, {
      query,
    }),
  appointment: (slug: string, id: string) =>
    api<AppointmentDetail>(`${base(slug)}/appointments/${id}`),
  contactAppointments: (slug: string, contactId: string) =>
    api<{ appointments: Appointment[] }>(
      `${base(slug)}/contacts/${contactId}/appointments`,
    ),
  book: (
    slug: string,
    body: {
      typeId: string;
      contactId: string;
      startsAt: string;
      conversationId?: string | null;
      memberId?: string | null;
      answers?: Record<string, string>;
      notes?: string | null;
      force?: boolean;
      /** false holds the confirmation notice (reminders still go). */
      confirm?: boolean;
    },
  ) =>
    api<{ appointment: Appointment }>(`${base(slug)}/appointments`, {
      method: "POST",
      body,
    }),
  reschedule: (
    slug: string,
    id: string,
    body: { startsAt: string; memberId?: string | null; force?: boolean },
  ) =>
    api<{ appointment: Appointment }>(
      `${base(slug)}/appointments/${id}/reschedule`,
      {
        method: "POST",
        body,
      },
    ),
  cancel: (
    slug: string,
    id: string,
    body: { reason?: string | null; notify?: boolean } = {},
  ) =>
    api<{ appointment: Appointment }>(
      `${base(slug)}/appointments/${id}/cancel`,
      {
        method: "POST",
        body,
      },
    ),
  setStatus: (
    slug: string,
    id: string,
    status: "confirmed" | "attended" | "no_show",
  ) =>
    api<{ appointment: Appointment }>(
      `${base(slug)}/appointments/${id}/status`,
      {
        method: "POST",
        body: { status },
      },
    ),
  setNotes: (slug: string, id: string, notes: string | null) =>
    api<{ appointment: Appointment }>(`${base(slug)}/appointments/${id}`, {
      method: "PATCH",
      body: { notes },
    }),
  busy: (
    slug: string,
    query: { from: string; to: string; agendaId?: string },
  ) => api<{ busy: BusyBlock[] }>(`${base(slug)}/scheduling/busy`, { query }),
  report: (
    slug: string,
    query: {
      from?: string;
      to?: string;
      timezone?: string;
      agendaId?: string;
    } = {},
  ) => api<SchedulingReport>(`${base(slug)}/reports/scheduling`, { query }),

  myCalendars: (slug: string) =>
    api<{
      connections: CalendarConnection[];
      providers: { google: boolean; microsoft: boolean };
      /** The one-time approval link for a company's IT (Outlook in Microsoft 365). */
      adminConsentUrl?: string;
    }>(`${base(slug)}/me/calendars`),
  startOAuth: (slug: string, provider: "google" | "microsoft") =>
    api<{ url: string; adminConsentUrl?: string }>(
      `${base(slug)}/me/calendars/oauth/${provider}`,
      { method: "POST" },
    ),
  connectCalDav: (
    slug: string,
    body: { url: string; username: string; password: string },
  ) =>
    api<{ connection: CalendarConnection }>(
      `${base(slug)}/me/calendars/caldav`,
      {
        method: "POST",
        body,
      },
    ),
  connectIcs: (slug: string, url: string) =>
    api<{ connection: CalendarConnection }>(`${base(slug)}/me/calendars/ics`, {
      method: "POST",
      body: { url },
    }),
  setCalendarChoices: (
    slug: string,
    id: string,
    calendars: Array<{ id: string; busy: boolean; write: boolean }>,
  ) =>
    api<{ connection: CalendarConnection }>(
      `${base(slug)}/me/calendars/${id}`,
      {
        method: "PATCH",
        body: { calendars },
      },
    ),
  syncCalendar: (slug: string, id: string) =>
    api<{ connections: CalendarConnection[] }>(
      `${base(slug)}/me/calendars/${id}/sync`,
      {
        method: "POST",
      },
    ),
  disconnectCalendar: (slug: string, id: string) =>
    api<{ ok: true }>(`${base(slug)}/me/calendars/${id}`, { method: "DELETE" }),

  holidays: (slug: string, year: number, optional: boolean) =>
    api<{ year: number; holidays: Array<{ date: string; code: string }> }>(
      `${base(slug)}/scheduling/holidays`,
      { query: { year, optional: optional ? 1 : 0 } },
    ),
  whatsappCalendar: (slug: string) =>
    api<{
      flows: Array<{
        accountId: string;
        flowId: string | null;
        status: "pending" | "published" | "failed";
        error: string | null;
      }>;
    }>(`${base(slug)}/scheduling/whatsapp-calendar`),
  setupWhatsappCalendar: (slug: string) =>
    api<{
      results: Array<{ accountId: string; status: string; error?: string }>;
    }>(`${base(slug)}/scheduling/whatsapp-calendar`, { method: "POST" }),
};
